import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Send, CheckCircle, FileText } from 'lucide-react';

export default function DagsordenForslag({ meetingType = 'ledelsen' }) {
  const { meetingId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || meetingId;
  const isMarketing = meetingType === 'marketing';
  const isSystem = meetingType === 'system';
  const isKundeservice = meetingType === 'kundeservice';
  const isProdukt = meetingType === 'produkt';
  const apiMap = { marketing: 'suggestions-marketing', system: 'suggestions-system', kundeservice: 'suggestions-kundeservice', produkt: 'suggestions-produkt' };
  const suggestionsApi = `/api/meetings/${apiMap[meetingType] || 'suggestions'}`;
  const submitApi = `${suggestionsApi}/submit`;
  const titleMap = { marketing: 'marketingmøde', system: 'systemmøde', kundeservice: 'kundeservicemøde', produkt: 'produktmøde' };
  const titleText = titleMap[meetingType] || 'ledelsesmøde';
  
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [suggestion, setSuggestion] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSuggestions = async () => {
      try {
        const res = await fetch(`${suggestionsApi}/${token}`);
        if (res.ok) {
          const data = await res.json();
          setSuggestions(data.suggestions || []);
        } else {
          setError('Ugyldigt eller udløbet link');
        }
      } catch {
        setError('Kunne ikke indlæse data');
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchSuggestions();
    else { setError('Manglende token'); setLoading(false); }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !subject.trim() || !suggestion.trim()) return;
    
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(submitApi, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, name: name.trim(), subject: subject.trim(), suggestion: suggestion.trim() })
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        setSuggestions(prev => [...prev, data.suggestion]);
        setSuggestion('');
        setSubmitted(true);
        setSubmitMessage(`Dit forslag er tilføjet til dagsordenen.`);
        setTimeout(() => { setSubmitted(false); setSubmitMessage(''); }, 5000);
      } else {
        setError(data.detail || `Fejl (${res.status}): Kunne ikke indsende forslaget`);
      }
    } catch (err) {
      setError(`Netværksfejl: ${err.message || 'Kunne ikke forbinde til serveren'}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#1E6F7A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error && !suggestions.length) {
    return (
      <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center p-4">
        <div className="bg-[#1A1D23] rounded-xl p-8 max-w-md w-full text-center border border-red-500/30">
          <p className="text-red-400 text-lg">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0C10] p-4 md:p-8" data-testid="suggestion-form-page">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Dagsordenspunkter</h1>
          <p className="text-gray-400">Indsend punkter til næste {titleText}</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-[#1A1D23] rounded-xl p-6 border border-white/10 mb-8">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Dit navn</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-[#0B0C10] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-[#1E6F7A] focus:outline-none"
                placeholder="Indtast dit navn"
                data-testid="suggestion-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Emne</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full bg-[#0B0C10] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-[#1E6F7A] focus:outline-none"
                placeholder="Kort emne for dit forslag"
                data-testid="suggestion-subject-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Dit forslag til dagsordenen</label>
              <textarea
                value={suggestion}
                onChange={(e) => setSuggestion(e.target.value)}
                rows={4}
                className="w-full bg-[#0B0C10] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-[#1E6F7A] focus:outline-none resize-none"
                placeholder="Beskriv det punkt du gerne vil have på dagsordenen..."
                data-testid="suggestion-text-input"
              />
            </div>
            <button
              type="submit"
              disabled={submitting || !name.trim() || !subject.trim() || !suggestion.trim()}
              className="w-full bg-[#1E6F7A] text-white py-3 px-6 rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-[#1E6F7A]/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="suggestion-submit-btn"
            >
              {submitting ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : submitted ? (
                <><CheckCircle className="w-5 h-5" /> Indsendt!</>
              ) : (
                <><Send className="w-5 h-5" /> Indsend forslag</>
              )}
            </button>
            
            {submitMessage && (
              <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-3 text-green-400 text-sm text-center">
                {submitMessage}
              </div>
            )}
            {error && (
              <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm text-center">
                {error}
              </div>
            )}
          </div>
        </form>

        {suggestions.length > 0 && (
          <div className="bg-[#1A1D23] rounded-xl p-6 border border-white/10">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#1E6F7A]" />
              Indsendte forslag ({suggestions.length})
            </h3>
            <div className="space-y-3">
              {suggestions.map((s, i) => (
                <div key={i} className="bg-[#0B0C10] rounded-lg p-4 border border-white/5">
                  <p className="text-white font-medium text-sm">{s.subject || ''}</p>
                  <p className="text-white/70 text-sm mt-1">{s.suggestion}</p>
                  <p className="text-gray-500 text-xs mt-2">— {s.name}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
